import React, { useState, useEffect, useCallback } from 'react'
import {
	Avatar,
	Button,
	Checkbox,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	MenuItem,
	TextField,
	CircularProgress,
	Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import moment from 'moment'
import PhotoDialog from './PhotoDialog'
import { STAFF_NOTE_TYPES, STAFF_NOTE_STATUS } from '../../../utils'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import { gray } from '../../../utils/colors'

const useStyles = makeStyles(() => ({
	dialog: {
		'& .MuiDialog-paper': {
			width: '920px',
			maxWidth: '920px'
		},
		'& .MuiDialogContent-root .MuiFilledInput-underline': {
			minWidth: 100
		},
		'& .MuiDialogContent-root .MuiTextField-root': {
			minWidth: 100
		},
		'& .MuiTextField-root': {
			minWidth: 100
		}
	},
	dialogActions: {
		justifyContent: 'flex-start'
	},
	petHeader: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		paddingTop: 16,
		paddingBottom: 16
	},
	petName: {
		fontSize: 16,
		fontWeight: '800',
		color: gray[900],
		marginLeft: 16
	},
	fieldHead: {
		fontSize: 12,
		fontWeight: '800',
		color: gray[900],
		marginTop: 4,
		marginBottom: 8,
		textTransform: 'capitalize'
	},
	borderedSection: {
		border: `1px solid ${gray[500]}`,
		borderRadius: 6,
		marginBottom: 10
	},
	tag: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		paddingTop: 8,
		paddingBottom: 8,
		cursor: 'pointer'
	},
	imagesContainer: {
		display: 'flex',
		flexDirection: 'row',
		padding: '.75rem',
		gap: '.5rem'
	},
	imageContainer: {
		height: '3rem',
		borderRadius: '0.375rem',
		cursor: 'pointer'
	},
	bod: {
		display: 'flex',
		flex: 1,
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'flex-start'
	},
	left: {
		width: '40%',
		paddingRight: 8,
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		justifyContent: 'flex-start'
	},
	right: {
		width: '60%',
		paddingLeft: 8,
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		justifyContent: 'flex-start'
	},
	noteContainer: {
		display: 'flex',
		backgroundColor: gray[200],
		borderRadius: 6,
		padding: 8,
		marginBottom: 16
	},
	noteTypeCol: {
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'flex-start'
	},
	noteTypeRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	noteTypeTitle: {
		fontSize: 12,
		fontWeight: '700',
		color: gray[700]
	},
	noteTypeText: {
		fontSize: 16,
		fontWeight: '600',
		color: gray[800]
	},
	noteDate: {
		fontSize: 14,
		fontWeight: '600',
		color: gray[600],
		marginLeft: 2
	}
}))

const EditNoteDialog = ({ isShown, setIsShown, runExport, loading, type, pet, note, visit, saveNote }) => {
	const classes = useStyles()
	const [ourNote, setNote] = useState({ tags: [] })
	const stati = [...Object.values(STAFF_NOTE_STATUS)]
	const types = [...Object.values(STAFF_NOTE_TYPES)]
	const tags = [
		{ name: 'Raise Alert', type: 'raise_alert' },
		{ name: 'Add to Check-out Screen', type: 'check_out' },
		{ name: 'Tag as Client Comment', type: 'client_comment' }
	]
	const bodSections = ['head', 'body_tail', 'legs_paws']

	const [showPhotoDialog, setShowPhotoDialog] = useState(false)
	const [currentPhoto, setCurrentPhoto] = useState('')
	const { allStaff } = useSelectedFacility()

	const noteAuthor = (() => {
		if (!note) {
			return 'Note Error'
		}
		if (!!note.automated_alert) {
			return 'Automated'
		}
		const staff = allStaff?.find((staff) => staff._id === note?.staff_id)
		if (staff) {
			return staff.full_name || `${staff.first_name} ${staff.last_name}`
		}
		if (note.staff_id) {
			return `Staff ID: ${note.staff_id}`
		}
		return 'Not Recorded'
	})()

	const lastUpdated = (() => {
		const history = note?.update_history
		if (!history || history.length === 0) {
			return null
		}
		const latest = history.reduce((a, b) => (moment(b.date).isAfter(moment(a.date)) ? b : a))
		const staff = allStaff?.find((staff) => staff._id === latest.staff_id)
		const name = staff
			? staff.full_name || `${staff.first_name} ${staff.last_name}`
			: latest.staff_id
			? `Staff ID: ${latest.staff_id}`
			: 'Not Recorded'
		return { name, date: latest.date }
	})()

	const handleShowPhotoDialog = useCallback((image_url) => {
		setCurrentPhoto(image_url)
		setShowPhotoDialog(true)
	}, [])

	const handleHidePhotoDialog = useCallback(() => {
		setCurrentPhoto('')
		setShowPhotoDialog(false)
	}, [])

	useEffect(() => {
		if (note && (!note._id || note._id !== ourNote._id)) {
			setNote({ ...note, status: note.status || 'Open' })
		}
	}, [note])

	if (!pet || !note) return null

	const setTag = (tag) => {
		let tags = ourNote?.tags || []
		let newTags
		if (tags.includes(tag)) {
			newTags = tags.filter((t) => t !== tag)
		} else {
			newTags = [...tags, tag]
		}
		setNote({
			...ourNote,
			tags: newTags
		})
	}

	const getBodyCheck = () => {
		if (!visit) {
			return null
		}
		let type = ourNote.entry ? 'body_check_entry' : 'body_check_exit'
		let ourType = visit[type]
		if (!ourType) return null
		let ourBod = visit[type][pet._id]
		if (!ourBod) return null
		let sections = Object.keys(ourBod).filter(
			(s) => bodSections.includes(s) && !ourBod[s].noIssues && typeof ourBod[s] === 'object'
		)
		return sections.map((s) => {
			let str = ''
			let subs = Object.keys(ourBod[s]).filter((d) => d !== 'noIssues' && d !== 'notes')
			subs.forEach((sub, i) => {
				let severity = ourBod[s][sub]
				if (severity && severity.issue) {
					str += `${sub} - ${severity.severity} severity; `
				}
			})

			return (
				<div>
					<Typography variant="body1" className={classes.fieldHead}>
						{s.replace('_', ' & ')}
					</Typography>
					<div className={clsx(classes.noteContainer, classes.noteTypeCol)}>
						<Typography className={classes.noteTypeTitle}>Notes</Typography>
						<Typography className={classes.noteTypeText}>
							{str}
							{ourBod[s].notes}
						</Typography>
					</div>
				</div>
			)
		})
	}

	return (
		<>
			<PhotoDialog
				style={{ zIndex: 10000 }}
				photoUrl={currentPhoto}
				isShown={showPhotoDialog}
				setIsShown={handleHidePhotoDialog}
				photoType="Reference Photo"
			/>
			<Dialog
				open={isShown}
				onClose={() => setIsShown()}
				aria-labelledby="form-dialog-title"
				className={classes.dialog}>
				{note._id ? (
					<DialogTitle>{note.title || 'STAFF NOTE'}</DialogTitle>
				) : (
					<DialogTitle>ADD NOTE</DialogTitle>
				)}

				<DialogContent>
					<div className={classes.petHeader}>
						<Avatar
							alt={pet.name}
							src={
								pet.profile_image_file_name
									? `https://waggle-keys-prod.s3.amazonaws.com/${pet.profile_image_file_name}`
									: null
							}
						/>
						<Typography variant="body1" className={classes.petName}>
							{pet.name}
						</Typography>
					</div>
					<div className={classes.bod}>
						<div className={classes.left}>
							<Typography variant="body1" className={classes.fieldHead}>
								STATUS
							</Typography>
							<TextField
								label="Status"
								variant="filled"
								select
								value={ourNote.status}
								onChange={(e) => setNote({ ...ourNote, status: e.target.value })}
								style={{ marginBottom: 12, width: '100%' }}>
								{stati.map((s) => {
									return (
										<MenuItem key={s} value={s}>
											{s}
										</MenuItem>
									)
								})}
							</TextField>
							{ourNote.type !== 'Alert' ? (
								<React.Fragment>
									<Typography variant="body1" className={classes.fieldHead}>
										TAG
									</Typography>
									<div className={classes.borderedSection}>
										{tags.map((t, i) => {
											let checked = ourNote.tags && ourNote.tags.includes(t.type)
											return (
												<div
													className={classes.tag}
													onClick={() => setTag(t.type)}
													style={{
														borderBottom:
															i === tags.length - 1 ? 'none' : `1px solid ${gray[500]}`
													}}>
													<Checkbox checked={checked ? true : false} />
													<Typography
														style={{
															color: gray[900],
															fontSize: 16
														}}>
														{t.name}
													</Typography>
												</div>
											)
										})}
									</div>
								</React.Fragment>
							) : null}
							{ourNote.images && ourNote.images.length > 0 ? (
								<>
									<Typography variant="body1" className={classes.fieldHead}>
										REFERENCE PHOTOS
									</Typography>
									<div className={clsx([classes.borderedSection, classes.imagesContainer])}>
										{ourNote.images.map((image, i) => {
											const { image_url } = image
											return (
												<img
													onClick={() => handleShowPhotoDialog(image_url)}
													key={`referece_photo-${i}`}
													src={image_url}
													className={classes.imageContainer}
												/>
											)
										})}
									</div>
								</>
							) : null}
						</div>

						<div className={classes.right}>
							{ourNote._id && ourNote.type ? (
								<Typography variant="body1" className={classes.fieldHead}>
									NOTE TYPE
								</Typography>
							) : null}
							{ourNote._id && ourNote.type ? (
								<div className={clsx(classes.noteContainer, classes.noteTypeCol)}>
									<Typography className={classes.noteTypeTitle}>Type</Typography>
									<Typography className={classes.noteTypeText}>{ourNote.type}</Typography>
								</div>
							) : null}
							{ourNote._id && ourNote.description ? (
								<Typography variant="body1" className={classes.fieldHead}>
									NOTE CONTENT
								</Typography>
							) : null}
							{ourNote._id && ourNote.description ? (
								<div className={clsx(classes.noteContainer, classes.noteTypeCol)}>
									<Typography className={classes.noteTypeTitle}>Notes</Typography>
									<Typography className={classes.noteTypeText}>{ourNote.description}</Typography>
								</div>
							) : null}
							{ourNote.alert_type && ourNote.alert_type === 'body_check' ? getBodyCheck() : null}

							{!ourNote._id ? (
								<React.Fragment>
									<Typography variant="body1" className={classes.fieldHead}>
										NOTE TYPE
									</Typography>
									<TextField
										label="Type"
										variant="filled"
										select
										value={ourNote.type}
										onChange={(e) => setNote({ ...ourNote, type: e.target.value })}
										style={{ marginBottom: 16, width: '100%' }}>
										{types.map((s) => {
											return (
												<MenuItem key={s} value={s}>
													{s}
												</MenuItem>
											)
										})}
									</TextField>
								</React.Fragment>
							) : null}
							{!ourNote._id ? (
								<React.Fragment>
									<Typography variant="body1" className={classes.fieldHead}>
										NOTE CONTENT
									</Typography>
									<TextField
										label="Note..."
										variant="filled"
										multiline={true}
										value={ourNote.description}
										onChange={(e) => setNote({ ...ourNote, description: e.target.value })}
										style={{ marginBottom: 16, width: '100%' }}
									/>
								</React.Fragment>
							) : null}
							{ourNote._id ? (
								<React.Fragment>
									<Typography variant="body1" className={classes.fieldHead}>
										NOTE AUTHOR
									</Typography>
									<div className={clsx(classes.noteContainer, classes.noteTypeRow)}>
										<Typography className={classes.noteTypeText}>{noteAuthor}</Typography>
										<Typography className={classes.noteDate}>
											{moment(ourNote.createdAt).format('MMM D, hh:mma')}
										</Typography>
									</div>
								</React.Fragment>
							) : null}
							{lastUpdated ? (
								<React.Fragment>
									<Typography variant="body1" className={classes.fieldHead}>
										LAST UPDATED BY
									</Typography>
									<div className={clsx(classes.noteContainer, classes.noteTypeRow)}>
										<Typography className={classes.noteTypeText}>
											{lastUpdated.name}
										</Typography>
										<Typography className={classes.noteDate}>
											{moment(lastUpdated.date).format('MMM D, hh:mma')}
										</Typography>
									</div>
								</React.Fragment>
							) : null}
						</div>
					</div>
				</DialogContent>
				<DialogActions className={classes.dialogActions}>
					<Button
						variant="contained"
						onClick={(e) => {
							e.stopPropagation()
							saveNote(ourNote)
						}}
						color="secondary"
						disabled={loading}>
						{loading ? <CircularProgress /> : ourNote._id ? 'SAVE' : 'CREATE NOTE'}
					</Button>
					<Button
						variant="outlined"
						onClick={(e) => {
							e.stopPropagation()
							setIsShown(false)
						}}
						color="secondary">
						CANCEL
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default EditNoteDialog
